import sortBy from 'lodash/sortBy';

import * as SyncService from '../SyncService';

import {
  DataSyncStatus,
  getAllDataSyncObjects,
  removeMultipleDataSyncObjectsWithId
} from '../LocalStore';

import getDSOsWithRemoteStatus from '../utils/getDSORemoteStatus';
import handleErrorDSOs from '../utils/handleErrorDSOs';
import { interactionsBlocked } from '../../blockUserInteractionHelper';
import updateDSOFieldAndSave from '../utils/updateDSOFieldAndSave';

const FETCH_DATASYNC_STATUS_TIMEOUT = 30 * 1000;
const RESEND_NOT_RECEIVED_MESSAGE_TIMEOUT = 20 * 60 * 1000;

let TimeOutDSOsCheckerOn = false;
let onGoingSyncStuckDSOs = false;

function getCommandsLength(DSOs) {
  let commandsLength = 0;
  DSOs.forEach(dso => {
    commandsLength += (dso.commands && dso.commands.length) || 0;
  });

  return commandsLength;
}

function getEarliestKinesisWriteTS(DSOs) {
  return sortBy(DSOs, 'kinesisWriteTS')[0].kinesisWriteTS;
}

function getDataSyncIds(dataSyncObjects) {
  return dataSyncObjects.map(dso => dso.id);
}

function writeErroredDataSyncObjectToAnalytics(errorDSOs, errorType) {
  if (!errorDSOs || errorDSOs.length < 1) {
    return;
  }
  window.analytics &&
    window.analytics.track('DSO_WITH_ERROR', {
      errorDSOIds: getDataSyncIds(errorDSOs),
      errorDSOCommandLength: getCommandsLength(errorDSOs)
    });
  const errorCommandsLength = getCommandsLength(errorDSOs);
  const earliestKinesisWriteTimestamp = getEarliestKinesisWriteTS(errorDSOs);
  const lagInSeconds = (Date.now() - earliestKinesisWriteTimestamp) / 1000;
  const error = new Error('Error in datasync object');
  error.name = errorType;
  window.bugsnagClient &&
    window.bugsnagClient.notify(error, {
      metaData: {
        fetchDatasyncStatusTimeout: FETCH_DATASYNC_STATUS_TIMEOUT,
        lagInSeconds,
        earliestKinesisWriteTimestamp,
        totalCommands: errorCommandsLength,
        errorDSOs: JSON.stringify(errorDSOs)
      }
    });
}

function getDSOWrittenBeforeTimestamp(dataSyncObjects, timeStamp) {
  return dataSyncObjects.filter(
    DSO =>
      DSO.status !== DataSyncStatus.QUEUED && DSO.kinesisWriteTS && DSO.kinesisWriteTS < timeStamp
  );
}

function getDSOsWithStatus(dataSyncObjects) {
  const timedOutDSOs = getDSOWrittenBeforeTimestamp(
    dataSyncObjects,
    Date.now() - FETCH_DATASYNC_STATUS_TIMEOUT
  );

  if (timedOutDSOs.length < 1) {
    return {};
  }

  window.analytics &&
    window.analytics.track('TIMED_OUT_DSO_FOUND', {
      timedOutProcessingDSOIds: getDataSyncIds(timedOutDSOs),
      commandsLength: getCommandsLength(timedOutDSOs)
    });

  return getDSOsWithRemoteStatus(timedOutDSOs);
}

// eslint-disable-next-line no-unused-vars
async function handleNotReceivedDSOs(notReceivedDSOs) {
  const timedOutDSOs = getDSOWrittenBeforeTimestamp(
    notReceivedDSOs,
    Date.now() - RESEND_NOT_RECEIVED_MESSAGE_TIMEOUT
  );
  if (timedOutDSOs.length < 1) {
    return;
  }

  writeErroredDataSyncObjectToAnalytics(timedOutDSOs, 'MESSAGE_NOT_RECEIVED_ON_KINESIS');

  await updateDSOFieldAndSave(timedOutDSOs, {
    status: DataSyncStatus.QUEUED,
    kinesisWriteTS: null
  });
}

async function syncStuckDSOs() {
  if (!navigator.onLine) {
    return;
  }
  if (interactionsBlocked()) {
    return;
  }
  if (onGoingSyncStuckDSOs) {
    return;
  }

  onGoingSyncStuckDSOs = true;
  try {
    const dataSyncObjects = await getAllDataSyncObjects();

    const {
      // Remove from local db
      succeededDSOs = [],
      // Replay commands and send new DSO
      versionMismatchErrorDSOs = [],
      // Write to analytics
      otherErrorDSOs = [],
      // Retry sending them to kinesis if older than RESEND_NOT_RECEIVED_MESSAGE_TIMEOUT
      notReceivedDSOs = [] // eslint-disable-line no-unused-vars
    } = await getDSOsWithStatus(dataSyncObjects);

    // Remove successfull DSOs
    await removeMultipleDataSyncObjectsWithId(getDataSyncIds(succeededDSOs));

    // await handleNotReceivedDSOs(notReceivedDSOs);

    // Log error DSOs to analytics
    writeErroredDataSyncObjectToAnalytics(otherErrorDSOs, 'UNKNOWN_ERROR_DATASYNC_CALLBACK');
    writeErroredDataSyncObjectToAnalytics(
      versionMismatchErrorDSOs,
      'VERSION_MISMATCH_ERROR_DATASYNC_CALLBACK'
    );

    // Bail out as no version_mismatch, nothing to do now.
    if (versionMismatchErrorDSOs.length < 1) {
      return;
    }

    await handleErrorDSOs(versionMismatchErrorDSOs.map(dso => dso.dataSyncStatus));
    SyncService.sync();
  } catch (e) {
    console.log('Error in syncStuckDSOs', e);
  } finally {
    onGoingSyncStuckDSOs = false;
  }
}

export const initTimeOutDSOsChecker = () => {
  if (TimeOutDSOsCheckerOn) {
    return;
  }

  TimeOutDSOsCheckerOn = true;
  syncStuckDSOs();
  setInterval(syncStuckDSOs, FETCH_DATASYNC_STATUS_TIMEOUT);
};
